import { Lesson, curve, ellipse, type LessonLevel, type XY, K, R, Y, G, W } from './drawing-lesson-brushes';

/** 草木与小生命。三级分别决定姿态、视角和光源，所有色面保留真实排笔。 */
const paint = (d: Lesson, p: XY[], color: string, edge = color, width = 5) => {
  d.fill(p, color); d.stroke([...p, p[0]], edge, width, d.level ? 'ink' : 'pen', 1, true);
};
const transform = (p: XY[], x: number, y: number, scale = 1, angle = 0): XY[] => p.map(([a, b]) => [x + scale * (a * Math.cos(angle) - b * Math.sin(angle)), y + scale * (a * Math.sin(angle) + b * Math.cos(angle))]);
const blade = (x: number, y: number, dx: number, dy: number): XY[] => curve([x, y], [[x + dx * .1 - dy * .28, y + dy * .1 + dx * .28], [x + dx * .72 - dy * .22, y + dy * .72 + dx * .22], [x + dx, y + dy]], [[x + dx * .72 + dy * .2, y + dy * .72 - dx * .2], [x + dx * .15 + dy * .25, y + dy * .15 - dx * .25], [x, y]]);
function leaf(d: Lesson, x: number, y: number, dx: number, dy: number, color: string, vein: string) {
  paint(d, blade(x, y, dx, dy), color);
  d.line(curve([x, y], [[x + dx * .3, y + dy * .3], [x + dx * .7, y + dy * .7], [x + dx, y + dy]]), vein, 4);
}
function spiral(cx: number, cy: number, radius: number, turns = 2.05): XY[] {
  return Array.from({ length: 120 }, (_, i): XY => { const t = i / 119, a = -.45 + t * Math.PI * 2 * turns, r = radius * (1 - t * .92); return [cx + Math.cos(a) * r, cy + Math.sin(a) * r]; });
}

// 蝴蝶：正面认识对称 → 斜飞花枝 → 月下侧翼特写。
function butterflyBeginner(d: Lesson) {
  d.step('左右展开的大翅膀', '先找身体的中线，两片上翅像向外张开的水滴，根部都接到中线。');
  for (const s of [-1, 1]) {
    const p = curve([0, 20], [[s * 65, -125], [s * 236, -284], [s * 284, -208]], [[s * 340, -106], [s * 247, 67], [0, 85]]);
    paint(d, transform(p, 500, 440), R, K, 7);
  }
  d.step('小翅膀托在下面', '下翅更圆更小，也从身体两边展开。先画后面的翅膀，再画身体。');
  for (const s of [-1, 1]) {
    paint(d, transform(curve([0, 60], [[s * 123, 50], [s * 290, 104], [s * 251, 214]], [[s * 212, 328], [s * 57, 256], [0, 127]]), 500, 440), Y, K, 7);
  }
  d.step('身体、触角和眼睛', '身体细长，中间宽两头收。两根触角向外弯，眼睛点在头部。');
  paint(d, ellipse(500, 508, 30, 137), K);
  paint(d, ellipse(500, 376, 40, 38), K);
  d.line(curve([484, 352], [[482, 305], [435, 291], [428, 318]]), K, 7);
  d.line(curve([517, 352], [[520, 305], [565, 291], [572, 318]]), K, 7);
  d.dot(486, 369, 10, W).dot(513, 369, 10, W);
  d.step('顺着翅膀画花纹', '花纹跟着翅膀的方向变长，左右呼应，边上留下干净的红色。');
  for (const s of [-1, 1]) {
    paint(d, transform(ellipse(s * 189, -86, 56, 90), 500, 440, 1, s * .24), Y, K, 5);
    paint(d, ellipse(500 + s * 173, 613, 39, 52), R, K, 5);
    d.line(curve([500 + s * 49, 493], [[500 + s * 83, 471], [500 + s * 108, 415], [500 + s * 122, 374]]), K, 5);
    d.line(curve([500 + s * 54, 557], [[500 + s * 69, 593], [500 + s * 90, 628], [500 + s * 123, 649]]), K, 5);
    d.line(curve([500 + s * 211, 268], [[500 + s * 237, 270], [500 + s * 252, 300], [500 + s * 250, 327]]), W, 10);
  }
}
function butterflyAdvanced(d: Lesson) {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = t.paper;
  const at = (p: XY[]) => transform(p, 420, 381, .85, -.38);
  d.step('一枝向上开的花', '花枝从右下伸向蝴蝶，花头偏在画面的右侧，给飞行方向留出空白。');
  d.stroke(curve([759, 950], [[700, 794], [787, 649], [781, 570]]), c[3], 15, 'ink');
  leaf(d, 742, 794, 165, -142, c[3], t.light); leaf(d, 744, 858, -199, -85, c[3], t.light);
  for (let i = 0; i < 8; i++) {
    const a = i * Math.PI / 4;
    paint(d, transform(ellipse(0, -68, 32, 71), 783, 560, 1, a), c[4], c[0], 3);
  }
  paint(d, ellipse(783, 560, 40, 38), c[2]);
  d.step('倾斜展开的翅膀', '身体先不画，让四片翅膀在同一个根部会合，远侧的翅膀收窄一点。');
  paint(d, at(curve([0, 36], [[47, -142], [189, -284], [278, -224]], [[353, -170], [331, 47], [246, 98]], [[165, 139], [78, 109], [0, 86]])), c[4], c[0]);
  paint(d, at(curve([0, 44], [[-68, -157], [-215, -269], [-280, -188]], [[-332, -97], [-285, 71], [-202, 103]], [[-140, 122], [-60, 111], [0, 97]])), c[1], c[0]);
  paint(d, at(curve([0, 83], [[117, 34], [288, 78], [247, 200]], [[210, 307], [66, 256], [0, 134]])), c[1], c[0]);
  paint(d, at(curve([0, 82], [[-93, 72], [-251, 122], [-196, 224]], [[-145, 300], [-42, 213], [0, 129]])), c[4], c[0]);
  d.step('翅面像叶子一样分区', '浅色大面靠近光源，深色脉络从根部向外散开。每片翅膀都保留完整的外缘。');
  for (const s of [-1, 1]) {
    const q = curve([s * 40, 36], [[s * 92, -78], [s * 153, -150], [s * 210, -171]], [[s * 228, -106], [s * 166, -25], [s * 40, 36]]);
    paint(d, at(q), s < 0 ? t.light : t.accent);
    d.stroke(at(curve([s * 47, 51], [[s * 113, 7], [s * 198, -36], [s * 255, -90]])), c[0], 6, 'ink', .75);
    d.stroke(at(curve([s * 42, 101], [[s * 81, 140], [s * 145, 172], [s * 195, 178]])), c[0], 6, 'ink', .75);
    d.stroke(at(curve([s * 91, -18], [[s * 135, -18], [s * 175, -2], [s * 200, 18]])), c[0], 4, 'ink', .6);
    d.stroke(at(curve([s * 57, 102], [[s * 96, 105], [s * 143, 119], [s * 170, 145]])), t.light, 25, 'translucent', .5);
    d.stroke(at(curve([s * 138, -159], [[s * 163, -185], [s * 193, -200], [s * 211, -196]])), c[5], 21, 'crayon', .72);
  }
  d.step('身体与向花朵伸出的触角', '胸部盖住四片翅膀的交点，触角朝飞行的方向卷起，六只小足贴在身体两侧。');
  paint(d, at(ellipse(0, 100, 22, 97)), c[0]); paint(d, at(ellipse(0, 14, 29, 32)), c[0]);
  for (const s of [-1, 1]) {
    d.stroke(at(curve([s * 10, -10], [[s * 36, -72], [s * 71, -89], [s * 88, -63]])), c[0], 5, 'ink');
    for (let i = 0; i < 3; i++) d.stroke(at(curve([s * 13, 49 + i * 24], [[s * 42, 47 + i * 23], [s * 49, 71 + i * 25], [s * 62, 82 + i * 26]])), c[0], 3, 'ink');
  }
  d.stroke(at(curve([-8, 52], [[-15, 99], [-12, 147], [-3, 164]])), t.light, 7, 'ink');
  d.step('轻薄的光与花蕊', '在翅尖留下柔亮的弧线，花蕊稍微加深，远处不再加零碎装饰。');
  d.stroke(at(curve([204, -224], [[249, -249], [281, -212], [279, -166]])), t.light, 25, 'translucent', .42);
  d.stroke(at(curve([-224, -147], [[-258, -91], [-239, -40], [-216, -10]])), c[5], 24, 'translucent', .35);
  d.stroke(at(curve([175, 243], [[211, 242], [234, 222], [239, 192]])), c[2], 22, 'translucent', .4);
  d.stroke(curve([765, 552], [[775, 546], [790, 547], [801, 552]]), t.shade, 10, 'crayon', .65);
  d.dot(774, 570, 6, c[0]).dot(791, 569, 6, c[0]);
  d.stroke(curve([724, 625], [[753, 645], [779, 649], [808, 632]]), t.accent, 22, 'translucent', .25);
}
function butterflyArt(d: Lesson) {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = c[0];
  d.step('暮紫色的花园', '深紫底色托住暖色翅膀，右上方的柔圆是远处月亮，前景花枝从下方长上来。');
  paint(d, ellipse(811, 194, 108, 108), t.accent);
  d.stroke(ellipse(811, 194, 120, 120), c[4], 30, 'translucent', .22, true);
  paint(d, blade(105, 1000, 169, -376), c[3]); paint(d, blade(888, 1000, -82, -374), c[4]);
  d.step('花枝托住落脚的位置', '弯弯的茎顶着一个侧面的花头，花瓣从一个花托伸出，蝴蝶会落在它的上沿。');
  d.stroke(curve([586, 1000], [[634, 872], [557, 771], [641, 678]]), c[3], 20, 'ink');
  leaf(d, 597, 887, -283, -135, c[3], t.accent);
  leaf(d, 607, 932, 222, -98, c[3], t.accent);
  for (let i = 0; i < 7; i++) {
    const a = -.92 + i * .29;
    paint(d, transform(blade(0, 0, 0, -124), 648, 716, 1, a), i % 2 ? t.shade : c[1]);
  }
  paint(d, ellipse(646, 704, 60, 21), c[2]);
  d.step('侧着合拢的巨大前翅', '从身体向左上展开一片长前翅，边缘有缓缓的折转，前翅和后翅的根部相连。');
  const front = curve([625, 589], [[589, 420], [451, 199], [235, 115]], [[173, 87], [132, 125], [153, 196]], [[171, 264], [139, 338], [208, 410]], [[252, 442], [304, 455], [333, 505]], [[362, 548], [432, 519], [489, 546]], [[543, 568], [579, 584], [625, 589]]);
  paint(d, front, c[1], t.shade, 7);
  d.step('后翅和渐浅的翅面', '圆圆的后翅向左下垂，浅色翅面顺着前翅长起来，边缘仍留一圈珊瑚色。');
  const hind = curve([623, 585], [[530, 528], [406, 505], [322, 572]], [[237, 640], [323, 743], [410, 736]], [[497, 724], [531, 641], [623, 585]]);
  paint(d, hind, c[4], t.shade);
  paint(d, curve([558, 512], [[469, 385], [359, 247], [231, 174]], [[260, 235], [246, 293], [296, 329]], [[363, 360], [392, 442], [447, 449]], [[484, 457], [528, 494], [558, 512]]), t.light);
  paint(d, curve([560, 595], [[484, 554], [391, 560], [350, 599]], [[316, 636], [343, 689], [400, 683]], [[462, 677], [497, 623], [560, 595]]), t.accent);
  d.step('像树枝分叉的翅脉', '主脉从翅根向翅尖走，再分成几条支脉。线条要疏密有序，不能随意切成网格。');
  const veins = [
    curve([611, 578], [[473, 455], [346, 268], [203, 157]]),
    curve([486, 466], [[430, 379], [314, 363], [252, 353]]),
    curve([397, 357], [[370, 304], [289, 252], [201, 232]]),
    curve([327, 273], [[318, 234], [300, 194], [271, 157]]),
    curve([558, 535], [[490, 508], [453, 513], [420, 488]]),
    curve([604, 586], [[496, 611], [447, 669], [377, 718]]),
    curve([500, 617], [[441, 603], [374, 602], [321, 616]]),
    curve([461, 643], [[432, 659], [435, 700], [425, 725]]),
  ];
  veins.forEach(p => d.stroke(p, c[0], 5, 'ink', .82));
  for (const p of [curve([250, 209], [[299, 238], [331, 275], [359, 311]]), curve([326, 346], [[359, 377], [381, 413], [409, 429]]), curve([357, 640], [[373, 674], [415, 663], [438, 648]])]) d.stroke(p, c[5], 25, 'translucent', .36);
  d.step('细长身体真正落在花上', '身体斜向右上，三对足从胸部伸到花心，两根触角轻轻分开，画清接触点。');
  d.stroke(curve([546, 646], [[579, 630], [627, 587], [658, 553]]), t.shade, 28, 'ink');
  paint(d, ellipse(664, 544, 18, 21), t.light, c[0]);
  d.dot(670, 539, 7, c[0]);
  d.stroke(curve([671, 534], [[709, 500], [739, 478], [764, 485]]), t.light, 4, 'ink');
  d.stroke(curve([660, 527], [[681, 491], [709, 466], [734, 464]]), t.light, 4, 'ink');
  for (const p of [[[623, 590], [659, 629], [659, 689]], [[613, 603], [630, 646], [622, 690]], [[601, 612], [590, 657], [606, 695]]] as XY[][]) d.stroke(p, t.light, 4, 'ink');
  d.stroke(curve([553, 638], [[586, 617], [622, 584], [646, 560]]), c[2], 6, 'ink');
  d.step('月光只照向光的一边', '长长的亮边沿翅尖和花瓣走，背光处保留深色，大片留白的夜色让主体更安静。');
  d.stroke(curve([184, 154], [[231, 164], [294, 215], [338, 255]]), c[5], 23, 'translucent', .32);
  d.stroke(curve([351, 568], [[399, 535], [450, 543], [489, 553]]), t.light, 22, 'translucent', .3);
  d.stroke(curve([521, 593], [[492, 615], [478, 637], [457, 659]]), c[5], 23, 'translucent', .28);
  d.stroke(curve([214, 125], [[295, 156], [375, 232], [434, 314]]), t.light, 7, 'glow', .6);
  d.stroke(curve([612, 679], [[637, 672], [662, 673], [681, 681]]), c[5], 8, 'glow', .6);
  d.stroke(curve([358, 808], [[409, 831], [460, 849], [504, 860]]), t.accent, 22, 'translucent', .26);
}

// 蜗牛：圆壳正侧面 → 叶上向左探头 → 雨后斜枝上的攀爬。
function snailBeginner(d: Lesson) {
  d.step('贴地的长身体', '身体像一条低低的小船，右侧抬头，尾巴细细地收在左边。');
  const body = curve([198, 746], [[324, 682], [533, 745], [669, 657]], [[702, 632], [703, 555], [759, 557]], [[811, 561], [803, 628], [828, 681]], [[854, 735], [843, 782], [775, 791]], [[588, 810], [375, 804], [198, 746]]);
  paint(d, body, G, K, 7);
  d.step('背上一只圆圆的壳', '壳压在背上，下边被身体托住，用大圆弧画出厚厚的轮廓。');
  paint(d, ellipse(450, 515, 227, 227), Y, K, 8);
  d.step('一圈一圈转进去', '螺旋从壳外面慢慢绕向中心，线与线之间要留出相同的空隙。');
  d.line(spiral(450, 515, 172, 1.75), R, 20);
  d.line(curve([282, 478], [[290, 413], [335, 365], [392, 350]]), W, 17);
  d.step('触角和小表情', '长触角顶端有眼睛，短触角朝前伸。嘴巴只用一个小弯钩。');
  d.line(curve([738, 572], [[730, 520], [709, 482], [703, 459]]), G, 14);
  d.line(curve([771, 574], [[796, 525], [829, 498], [841, 472]]), G, 14);
  d.dot(703, 459, 29, W).dot(841, 472, 29, W).dot(707, 460, 12, K).dot(846, 474, 12, K);
  d.line(curve([792, 645], [[812, 658], [825, 653], [831, 641]]), K, 5);
  d.line(curve([233, 769], [[382, 803], [561, 819], [725, 812]]), K, 4);
}
function snailAdvanced(d: Lesson) {
  const c = d.theme.colors, t = d.theme; d.drawing.background = t.paper;
  d.step('一片宽阔的叶子', '叶尖朝向左下，叶片中线向右上弯，蜗牛的身体顺着这条中线趴下。');
  const bed = curve([130, 835], [[297, 613], [607, 610], [922, 686]], [[794, 819], [459, 933], [130, 835]]);
  paint(d, bed, c[3], c[0], 4);
  d.stroke(curve([143, 830], [[385, 791], [697, 745], [909, 692]]), t.accent, 7, 'ink');
  for (let i = 0; i < 5; i++) {
    d.stroke(curve([293 + i * 110, 807 - i * 19], [[318 + i * 110, 758 - i * 15], [339 + i * 105, 722 - i * 11], [386 + i * 92, 687 - i * 5]]), t.light, 4, 'ink', .5);
    d.stroke(curve([318 + i * 105, 804 - i * 18], [[375 + i * 99, 812 - i * 13], [414 + i * 94, 829 - i * 19], [455 + i * 89, 841 - i * 24]]), t.light, 4, 'ink', .5);
  }
  d.step('向左探出的身体', '左端的头高高抬起，背部要连贯地托住壳，右侧尾巴收成尖尖的一点。');
  const body = curve([859, 701], [[691, 672], [516, 713], [381, 647]], [[333, 625], [322, 559], [286, 546]], [[245, 531], [221, 557], [230, 605]], [[227, 660], [264, 710], [337, 736]], [[475, 777], [706, 770], [859, 701]]);
  paint(d, body, c[2], c[0]);
  d.stroke(curve([272, 666], [[386, 741], [641, 759], [797, 716]]), t.shade, 26, 'translucent', .28);
  d.step('椭圆贝壳的体积', '壳向后倾，用一块深底和一块暖亮面表现体积；下缘真正压住身体。');
  paint(d, ellipse(557, 494, 231, 217), c[0]);
  paint(d, ellipse(543, 475, 215, 205), c[1], t.shade);
  d.stroke(ellipse(548, 482, 183, 175, .05, 1.84), t.shade, 38, 'translucent', .37);
  d.stroke(ellipse(535, 472, 182, 170, 3.35, 4.75), t.light, 29, 'crayon', .7);
  d.step('螺旋和顺壳的纹理', '螺旋转进中间，亮纹也跟着圆壳的弧度走，不能画成随意的平行条纹。');
  d.stroke(spiral(543, 474, 171, 1.8), c[0], 11, 'ink', .8);
  for (let i = 0; i < 7; i++) {
    const a = 2.8 + i * .35;
    d.stroke(ellipse(543, 474, 198, 188, a, a + .18), c[2], 23, 'translucent', .28);
  }
  d.stroke(ellipse(543, 474, 114, 109, 3.55, 4.6), t.light, 22, 'crayon', .65);
  d.step('触角与叶上的露珠', '触角向前张开，蜗牛正在查看前方。露珠沿叶脉落下，用深底和同侧亮边画出透明感。');
  for (const p of [curve([253, 557], [[226, 513], [200, 469], [181, 447]]), curve([283, 547], [[283, 492], [272, 444], [252, 414]])]) d.stroke(p, c[2], 14, 'ink');
  d.dot(181, 447, 20, c[0]).dot(252, 414, 20, c[0]);
  d.dot(176, 442, 6, c[5]).dot(247, 410, 6, c[5]);
  d.stroke(curve([231, 607], [[216, 598], [203, 594], [191, 596]]), c[2], 7, 'ink');
  d.stroke(curve([234, 635], [[244, 644], [255, 643], [261, 633]]), c[0], 4, 'ink');
  for (const [x, y, r] of [[382, 829, 24], [720, 781, 18], [848, 710, 14]]) {
    paint(d, ellipse(x, y, r, r * .7), c[4]);
    d.stroke(ellipse(x, y, r * .65, r * .45, 3.3, 4.9), c[5], 5, 'ink');
  }
}
function snailArt(d: Lesson) {
  const c = d.theme.colors, t = d.theme; d.drawing.background = c[4];
  d.step('雨后的暖光', '天空用安静的蓝色，左上的圆光偏暖，斜枝把视线从左下带向右上。');
  paint(d, ellipse(208, 208, 112, 112), t.light);
  d.stroke(ellipse(208, 208, 128, 128), c[5], 28, 'translucent', .2, true);
  paint(d, curve([0, 907], [[305, 802], [682, 684], [1000, 464]], [[1000, 568], [545, 834], [0, 990]]), c[0]);
  d.stroke(curve([21, 925], [[365, 821], [724, 667], [986, 506]]), t.shade, 28, 'crayon', .75, true);
  d.step('前景的长叶', '两片叶子都从枝条上的节点长出，留出上方的攀爬位置，叶脉跟着弯曲的中线走。');
  leaf(d, 236, 866, 106, -276, c[3], t.light);
  leaf(d, 558, 758, 320, 65, t.accent, c[5]);
  leaf(d, 105, 915, 152, 75, c[3], t.light);
  d.step('顺着枝条爬的长身体', '尾巴留在左下，身体贴着枝面向右上爬，抬起的头和脚底连成一条完整轮廓。');
  const body = curve([316, 803], [[476, 748], [610, 708], [681, 618]], [[717, 574], [710, 500], [755, 486]], [[802, 471], [827, 515], [814, 556]], [[795, 626], [793, 651], [738, 685]], [[592, 759], [445, 807], [316, 803]]);
  paint(d, body, c[2], c[0]);
  d.stroke(curve([392, 788], [[520, 759], [658, 699], [737, 655]]), t.shade, 24, 'translucent', .4);
  d.step('背光的圆壳', '圆壳偏向左下，让头前方留出探索空间。壳口贴住背，亮面朝向左上光源。');
  paint(d, ellipse(520, 564, 203, 204), c[0]);
  paint(d, ellipse(507, 549, 190, 194), c[1], t.shade);
  d.stroke(ellipse(505, 548, 166, 173, -.2, 1.7), t.shade, 39, 'translucent', .36);
  d.stroke(ellipse(505, 548, 163, 172, 3.35, 4.95), t.light, 28, 'crayon', .7);
  d.step('壳上的旋纹与湿润高光', '螺旋是壳的生长方向，宽亮纹只画在向光的一侧，不把整个壳描成发光的圆。');
  d.stroke(spiral(507, 549, 151, 1.85), c[0], 11, 'ink', .82);
  for (let i = 0; i < 7; i++) d.stroke(ellipse(507, 549, 175, 180, 2.85 + i * .23, 3.02 + i * .23), c[2], 22, 'translucent', .3);
  d.stroke(ellipse(507, 549, 111, 113, 3.4, 4.65), c[5], 22, 'translucent', .45);
  d.stroke(ellipse(507, 549, 184, 191, 3.65, 4.85), c[5], 7, 'glow', .62);
  d.step('探向天空的触角', '两根长触角张成不同的方向，眼睛在顶端。两根短触角更靠近嘴，避免画成猫的胡须。');
  d.stroke(curve([750, 499], [[729, 446], [717, 408], [728, 377]]), c[2], 13, 'ink');
  d.stroke(curve([786, 501], [[823, 453], [853, 429], [883, 413]]), c[2], 13, 'ink');
  d.dot(728, 377, 20, c[0]).dot(883, 413, 20, c[0]);
  d.dot(724, 373, 5, c[5]).dot(879, 409, 5, c[5]);
  d.stroke(curve([807, 556], [[833, 548], [850, 551], [861, 561]]), t.light, 7, 'ink');
  d.stroke(curve([801, 575], [[819, 580], [829, 592], [831, 602]]), t.light, 7, 'ink');
  d.stroke(curve([777, 584], [[784, 591], [793, 590], [797, 583]]), c[0], 4, 'ink');
  d.stroke(curve([735, 546], [[726, 574], [720, 597], [705, 617]]), c[5], 10, 'crayon', .65);
  d.step('叶尖的雨滴与枝上的湿痕', '大雨滴悬在叶尖，反射光与天空相同。湿痕沿木纹拉长，最后只留几个明确的亮点。');
  const drop = curve([879, 823], [[888, 845], [915, 866], [902, 888]], [[887, 908], [855, 894], [858, 875]], [[859, 852], [875, 837], [879, 823]]);
  paint(d, drop, c[4], c[5], 3);
  d.stroke(curve([872, 860], [[866, 874], [868, 886], [878, 888]]), c[5], 5, 'glow', .8);
  d.stroke(curve([78, 948], [[153, 925], [217, 905], [282, 883]]), t.light, 6, 'ink', .75);
  d.stroke(curve([808, 634], [[863, 604], [918, 572], [967, 540]]), c[5], 6, 'glow', .52);
  d.stroke(curve([294, 709], [[306, 681], [317, 650], [323, 625]]), t.light, 22, 'translucent', .26);
}

// 蘑菇：伞状大形 → 高低错落的林地簇生 → 仰视菌褶与林下透光。
function mushroomBeginner(d: Lesson) {
  d.step('稳稳站住的菌柄', '菌柄上窄下宽，下端圆圆地落地，像一条站直的小裙子。');
  paint(d, curve([438, 459], [[453, 582], [432, 690], [398, 814]], [[444, 861], [570, 861], [613, 814]], [[577, 685], [560, 574], [575, 459]], [[534, 443], [484, 443], [438, 459]]), Y, K, 7);
  d.step('一把圆圆的红伞', '伞顶是大圆弧，下沿是一条浅弧线，左右都伸过菌柄。');
  paint(d, curve([182, 498], [[210, 155], [769, 150], [821, 498]], [[735, 575], [271, 575], [182, 498]]), R, K, 8);
  d.step('白斑顺着伞面长', '大的白斑靠中间，两侧的小白斑稍微压扁，不要平均排成一排。');
  for (const [x, y, rx, ry] of [[364, 326, 49, 35], [577, 302, 56, 35], [708, 411, 38, 44], [470, 463, 52, 35], [280, 454, 34, 27]]) paint(d, ellipse(x, y, rx, ry), W);
  d.step('菌柄的线和脚边的草', '几道细线顺着菌柄往下走，草从同一个根部冒出，别遮住蘑菇的脚。');
  d.line(curve([478, 589], [[480, 674], [466, 750], [453, 799]]), K, 4);
  d.line(curve([542, 590], [[539, 668], [552, 752], [565, 800]]), K, 4);
  for (const x of [289, 704]) for (let i = -1; i <= 1; i++) d.line(curve([x, 849], [[x + i * 12, 813], [x + i * 25, 790], [x + i * 37, 777]]), G, 10);
}
function mushroomAdvanced(d: Lesson) {
  const c = d.theme.colors, t = d.theme; d.drawing.background = t.paper;
  const cap = (x: number, y: number, r: number) => curve([x - r, y], [[x - r * .86, y - r * 1.13], [x + r * .82, y - r * 1.24], [x + r, y - 8]], [[x + r * .61, y + r * .18], [x - r * .59, y + r * .22], [x - r, y]]);
  d.step('林地上的柔软土坡', '土坡偏低，一根斜枝和几片叶子交代森林，给高低不同的蘑菇留位置。');
  paint(d, curve([79, 828], [[255, 740], [648, 723], [916, 784]], [[839, 879], [289, 914], [79, 828]]), t.accent);
  d.stroke(curve([175, 847], [[400, 810], [665, 790], [836, 816]]), c[3], 27, 'translucent', .3);
  leaf(d, 830, 785, 81, -188, c[3], c[2]); leaf(d, 769, 814, 135, -85, c[3], c[2]);
  d.step('后面高高的一把伞', '先画后面的大蘑菇，菌柄从伞中央一直连到土坡，伞沿遮住菌柄顶端。');
  paint(d, curve([598, 440], [[621, 542], [612, 658], [582, 774]], [[617, 799], [671, 792], [699, 767]], [[664, 647], [658, 540], [671, 433]], [[646, 424], [617, 426], [598, 440]]), c[5], t.shade);
  paint(d, ellipse(634, 442, 228, 49), t.shade);
  paint(d, cap(628, 422, 252), c[1], c[0]);
  d.stroke(curve([427, 376], [[466, 277], [586, 245], [679, 273]]), t.light, 24, 'translucent', .45);
  d.step('前面的两朵矮蘑菇', '中等的蘑菇放在左前，小蘑菇靠近它的脚边，前后遮挡形成一个不对称三角形。');
  paint(d, curve([336, 656], [[346, 712], [333, 774], [318, 817]], [[342, 841], [386, 837], [408, 817]], [[388, 769], [385, 714], [390, 653]], [[372, 642], [350, 644], [336, 656]]), c[2], t.shade);
  paint(d, ellipse(363, 650, 153, 35), t.shade);
  paint(d, cap(351, 632, 174), c[1], c[0]);
  paint(d, curve([206, 760], [[210, 794], [210, 822], [196, 847]], [[220, 860], [246, 855], [259, 843]], [[246, 812], [244, 786], [248, 760]], [[232, 753], [218, 753], [206, 760]]), c[5], t.shade);
  paint(d, cap(222, 751, 94), c[2], t.shade);
  d.step('顺伞面的斑纹与菌褶', '斑纹按透视变扁，伞沿下面的菌褶都指向菌柄；线条不穿过前面的蘑菇。');
  for (const [x, y, rx, ry] of [[510, 342, 34, 22], [656, 296, 40, 25], [759, 374, 40, 22], [594, 404, 43, 18], [288, 570, 29, 19], [402, 592, 34, 21]]) paint(d, ellipse(x, y, rx, ry), c[5]);
  for (let i = 0; i < 8; i++) {
    const x = 435 + i * 55;
    if (x < 585 || x > 701) d.stroke(curve([x, 455], [[x + (633 - x) * .12, 465], [x + (633 - x) * .25, 479], [x + (633 - x) * .36, 480]]), c[2], 3, 'ink');
  }
  d.stroke(curve([659, 500], [[647, 588], [658, 677], [672, 739]]), t.shade, 23, 'translucent', .2);
  d.stroke(curve([353, 688], [[349, 734], [346, 781], [338, 806]]), t.light, 23, 'translucent', .6);
  d.stroke(curve([190, 722], [[214, 699], [250, 707], [268, 723]]), c[5], 23, 'translucent', .46);
  d.step('亮边和脚边的新叶', '光从左上落下来，每把伞只补一段亮边，再用前景叶子压住土坡边缘。');
  d.stroke(curve([415, 372], [[445, 313], [482, 291], [522, 280]]), t.light, 23, 'crayon', .65);
  d.stroke(curve([215, 598], [[242, 551], [287, 531], [327, 527]]), t.light, 24, 'translucent', .4);
  d.stroke(curve([697, 424], [[750, 420], [813, 420], [850, 405]]), t.shade, 24, 'translucent', .3);
  leaf(d, 551, 869, -145, -58, c[3], c[2]); leaf(d, 554, 869, 111, -68, c[3], c[2]);
  d.stroke(curve([757, 848], [[794, 828], [829, 827], [859, 832]]), t.light, 22, 'crayon', .45);
}
function mushroomArt(d: Lesson) {
  const c = d.theme.colors, t = d.theme; d.drawing.background = '#12261E';
  d.step('林下深色与远处的光', '深林绿作夜色底，远处两道青绿树影竖着生长，右上的月光与主伞错开。');
  paint(d, curve([81, 0], [[139, 329], [115, 650], [45, 1000]], [[100, 1000], [184, 585], [169, 0]]), c[3]);
  paint(d, curve([853, 0], [[790, 388], [825, 739], [923, 1000]], [[975, 1000], [884, 510], [905, 0]]), t.accent);
  paint(d, ellipse(802, 149, 65, 65), c[2]);
  d.stroke(ellipse(802, 149, 75, 75), t.light, 24, 'translucent', .22, true);
  d.step('森林地面和远处的小伞', '地面缓缓起伏，远处的小蘑菇用安静的剪影，让近处的大伞成为主角。');
  paint(d, curve([0, 886], [[271, 836], [628, 869], [1000, 786]], [[1000, 1000], [0, 1000], [0, 886]]), c[3]);
  for (const [x, y, r] of [[157, 785, 91], [856, 754, 91]]) {
    d.stroke(curve([x, y + 11], [[x - 3, y + 40], [x + 5, y + 72], [x + 2, y + 100]]), t.accent, 20, 'ink');
    paint(d, curve([x - r, y], [[x - r * .6, y - r], [x + r * .7, y - r], [x + r, y]], [[x + 40, y + 16], [x - 40, y + 16], [x - r, y]]), t.shade);
  }
  d.step('从低处仰望的弯菌柄', '菌柄从地面向左上弯，越接近伞底越窄；用亮面和暗面组成圆柱，根部必须落地。');
  const stem = curve([481, 442], [[454, 578], [488, 742], [411, 915]], [[451, 943], [546, 943], [605, 914]], [[520, 774], [554, 596], [561, 442]], [[540, 427], [500, 428], [481, 442]]);
  paint(d, stem, c[2], t.shade);
  paint(d, curve([526, 486], [[517, 639], [526, 800], [577, 913]], [[556, 922], [537, 926], [516, 927]], [[473, 800], [503, 620], [498, 486]], [[508, 482], [516, 482], [526, 486]]), t.light);
  d.stroke(curve([481, 541], [[474, 670], [489, 781], [446, 897]]), t.shade, 25, 'translucent', .36);
  d.step('伞面与完整的伞底', '先画红色伞面，再用宽椭圆画我们仰望到的伞底，伞底必须连成一个完整的面。');
  const cap = curve([160, 399], [[204, 165], [574, 99], [799, 321]], [[851, 362], [846, 405], [806, 430]], [[626, 487], [357, 483], [160, 399]]);
  paint(d, cap, c[1], t.shade);
  paint(d, ellipse(498, 423, 338, 79), c[2], t.light);
  d.stroke(curve([210, 317], [[300, 211], [465, 185], [584, 225]]), t.light, 26, 'translucent', .3);
  d.step('伞底的放射菌褶', '菌褶从菌柄顶端向伞沿散开，弧线随着椭圆变长短。下边缘最亮，中心颜色稍深。');
  for (let i = 0; i < 25; i++) {
    const a = i * Math.PI * 2 / 25, x = 498 + Math.cos(a) * 320, y = 423 + Math.sin(a) * 64;
    const sx = 512 + Math.cos(a) * 37, sy = 430 + Math.sin(a) * 17;
    d.stroke(curve([sx, sy], [[sx + (x - sx) * .36, sy + (y - sy) * .2 - 5], [sx + (x - sx) * .72, sy + (y - sy) * .72], [x, y]]), t.shade, 4, 'ink', .7);
  }
  paint(d, ellipse(514, 433, 44, 18), t.light);
  d.stroke(ellipse(498, 423, 330, 73, .05, 3.08), c[5], 6, 'glow', .64);
  d.step('伞面上的散落白斑', '白斑贴着弯曲的伞顶，两侧更窄，中间更完整；用柔边亮面把上方的光带进来。');
  for (const [x, y, rx, ry, a] of [[296, 304, 35, 19, -.43], [438, 237, 44, 21, -.13], [603, 271, 38, 20, .3], [713, 338, 35, 19, .5], [440, 334, 48, 24, .1]]) paint(d, transform(ellipse(0, 0, rx, ry), x, y, 1, a), c[5]);
  d.stroke(curve([244, 288], [[294, 236], [354, 207], [407, 204]]), c[5], 23, 'translucent', .32);
  d.stroke(curve([520, 211], [[587, 227], [640, 255], [679, 283]]), t.light, 24, 'translucent', .3);
  d.stroke(curve([742, 370], [[776, 385], [794, 395], [807, 407]]), t.shade, 23, 'translucent', .45);
  d.step('近景叶片接住伞下的光', '前景叶片从地面长出，向光的叶脉明亮。菌柄只留一条细长反光，让夜色保持安静。');
  leaf(d, 209, 988, 130, -177, t.accent, c[2]); leaf(d, 758, 1000, -85, -179, c[3], c[2]);
  leaf(d, 681, 997, 161, -104, t.accent, t.light);
  // 高光沿菌柄右轮廓内收，不能成为悬在轮廓外的一条亮线。
  const stemLight = curve([561, 442], [[554, 596], [520, 774], [605, 914]]).slice(9, 21).map(([x, y]): XY => [x - 10, y]);
  d.stroke(stemLight, c[5], 6, 'translucent', .48);
  d.stroke(curve([326, 924], [[360, 915], [385, 918], [406, 928]]), c[2], 23, 'translucent', .4);
  d.stroke(curve([594, 937], [[624, 927], [650, 925], [671, 930]]), c[2], 25, 'translucent', .34);
}

export const NATURE_EXAMPLES = [
  { id: 'butterfly', title: '蝴蝶', skill: '对称翅膀 · 翅脉', keywords: ['蝴蝶', '彩蝶', '昆虫', '动物', '翅膀', '对称', '翅脉', '花园'], create: (level: LessonLevel) => {
    const d = new Lesson('butterfly', level); [butterflyBeginner, butterflyAdvanced, butterflyArt][level](d);
    return d.done('butterfly', '蝴蝶', '对称翅膀 · 翅脉', ['彩蝶', '昆虫', '动物', '翅膀', '对称', '翅脉', '花园']);
  } },
  { id: 'snail', title: '蜗牛', skill: '螺旋曲线 · 湿润质感', keywords: ['蜗牛', '小蜗牛', '动物', '软体动物', '螺旋', '圆壳', '雨滴', '叶子', '湿润质感'], create: (level: LessonLevel) => {
    const d = new Lesson('snail', level); [snailBeginner, snailAdvanced, snailArt][level](d);
    return d.done('snail', '蜗牛', '螺旋曲线 · 湿润质感', ['小蜗牛', '动物', '软体动物', '螺旋', '圆壳', '雨滴', '叶子', '湿润质感']);
  } },
  { id: 'mushroom', title: '蘑菇', skill: '伞形轮廓 · 放射菌褶', keywords: ['蘑菇', '小蘑菇', '菌菇', '食物', '森林', '伞形', '菌褶', '放射线', '透视'], create: (level: LessonLevel) => {
    const d = new Lesson('mushroom', level); [mushroomBeginner, mushroomAdvanced, mushroomArt][level](d);
    return d.done('mushroom', '蘑菇', '伞形轮廓 · 放射菌褶', ['小蘑菇', '菌菇', '食物', '森林', '伞形', '菌褶', '放射线', '透视']);
  } },
];
